import type {
    ChatbotMessageDto,
    ChatbotMessagesDto,
} from "@core/data/dto/chatbot-message.dto";
import {
    ChatbotMessageDtoSchema,
    ChatbotMessagesDtoSchema,
} from "@core/data/dto/chatbot-message.dto";
import type { ModelError } from "@core/domain/errors/model.error";
import { NonRecuperableModelError } from "@core/domain/errors/model.error";
import {
    ChatbotMessage,
    ChatbotMessages,
} from "@core/domain/models/chatbot-message.model";
import { injectable } from "inversify";
import type { Result } from "neverthrow";
import { err } from "neverthrow";

@injectable()
export class ChatbotMapper {
    map(
        chatbotDto: ChatbotMessageDto,
    ): Result<ChatbotMessage, ModelError<typeof ChatbotMessage>> {
        const chatbotDtoParsed = ChatbotMessageDtoSchema.safeParse(chatbotDto);

        if (!chatbotDtoParsed.success) {
            console.error(chatbotDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return ChatbotMessage.createWithRecovery({
            content: chatbotDto.content,
            role: chatbotDto.role,
        });
    }

    mapList(chatbotMessagesDto: ChatbotMessagesDto): ChatbotMessages {
        const chatbotMessagesDtoParsed =
            ChatbotMessagesDtoSchema.safeParse(chatbotMessagesDto);

        if (!chatbotMessagesDtoParsed.success) {
            console.error(chatbotMessagesDtoParsed.error.issues);
            return ChatbotMessages.empty();
        }

        const chatbotMessages =
            chatbotMessagesDto.history_conversation.mapNotNull((chatbotDto) =>
                this.map(chatbotDto).match(
                    (chatbotMessage) => chatbotMessage,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping chatbot message:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
            );

        return ChatbotMessages.create({
            chatbotMessages,
            count: chatbotMessages.length,
        }).unwrapOr(ChatbotMessages.empty());
    }
}
